import axios from '../axios'

/**
 * Quote follow-up emails (item 5, stage 1).
 *
 * Settings, steps and bank holidays are for users whose role has showSettings.
 * The per-quote actions are for the quote's salesperson or a showSettings user;
 * byOpp returns `canAct` so the screen can say which.
 *
 * Errors come back as 404/409 `{ error }` or 400 `{ errors: { field: message } }`. A permission refusal is a
 * 409 `{ error: 'You do not have permission to do this' }`, never a 403: the global axios interceptor treats
 * every 403 as a lost session and signs the user out.
 * `errorMessage(err)` turns either into one line to show the user.
 */
export default class FollowupService {
  // ---- settings ----
  getSettings() {
    return this._get('/v1/followupSettings')
  }

  saveSettings(settings) {
    return this._send('put', '/v1/followupSettings', settings)
  }

  // ---- steps ----
  /** The default steps, or with a project type id that quote type's own steps (batch 3). */
  getSteps(projectTypeId) {
    var id = projectTypeId === null || projectTypeId === undefined ? '' : '?projectTypeId=' + encodeURIComponent(projectTypeId)
    return this._get('/v1/followupSteps' + id)
  }

  saveStep(step) {
    return this._send('post', '/v1/followupSteps', step)
  }

  editStep(step) {
    if (!step || !step.id) return Promise.reject(new Error('Id must be available for editing.'))
    return this._send('put', '/v1/followupSteps/' + step.id, step)
  }

  deleteStep(id) {
    return this._send('delete', '/v1/followupSteps/' + id)
  }

  // ---- per quote type (batch 3) ----
  getTypes() {
    return this._get('/v1/followupTypes')
  }

  saveType(projectTypeId, body) {
    return this._send('put', '/v1/followupTypes/' + projectTypeId, body)
  }

  // ---- bank holidays ----
  getBankHolidays() {
    return this._get('/v1/bankHolidays')
  }

  saveBankHoliday(holiday) {
    return this._send('post', '/v1/bankHolidays', holiday)
  }

  editBankHoliday(holiday) {
    if (!holiday || !holiday.id) return Promise.reject(new Error('Id must be available for editing.'))
    return this._send('put', '/v1/bankHolidays/' + holiday.id, holiday)
  }

  deleteBankHoliday(id) {
    return this._send('delete', '/v1/bankHolidays/' + id)
  }

  // ---- per quote ----
  byOpp(oppId) {
    return this._get('/v1/quoteFollowup/byOpp/' + oppId)
  }

  enrol(quoteVerId) {
    return this._send('post', '/v1/quoteFollowup/enrol/' + quoteVerId)
  }

  approve(id) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/approve')
  }

  stop(id) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/stop')
  }

  reenable(id) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/reenable')
  }

  clearAttention(id) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/clearAttention')
  }

  /**
   * The email of unknown outcome (the follow-up's `uncertainStepNo`): `sent` true = "It was sent" (recorded as
   * sent, the follow-up moves on), false = "Send it again" (it goes again). 409 `{ error }` when refused.
   */
  resolveUncertain(id, sent) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/resolveUncertain', { sent: !!sent })
  }

  // ---- items (a) and (b), batch 3 ----
  /** "Skip this one": the follow-up's next email is skipped when it falls due (undo with unskip). */
  skipNext(id, stepNo) {
    // the step the person saw: if it has gone meanwhile the server refuses rather than skip the next one unseen
    return this._send('post', '/v1/quoteFollowup/' + id + '/skipNext', stepNo === undefined || stepNo === null ? {} : { stepNo: stepNo })
  }

  unskip(id) {
    return this._send('post', '/v1/quoteFollowup/' + id + '/unskip')
  }

  /** `{ quoteVerId?, contactId?, customerId?, date?: 'dd/MM/yyyy' }` -> the automatic emails due around a task/event. */
  dueFor(query) {
    return this._send('post', '/v1/quoteFollowups/dueFor', query)
  }

  /** `{ workingDays, until, allUsers, rows: [...] }`: the automatic emails going out in the next few working days. */
  goingOut() {
    return this._get('/v1/quoteFollowups/goingOut')
  }

  /** Item (e): `{ from?, to?, customer?, contact?, quoteNo?, userId?, page, size }` -> `{ total, page, size, allUsers, rows }`. */
  sentEmails(filter) {
    return this._send('post', '/v1/quoteFollowups/sentEmails', filter)
  }

  /** One sent email as it went, with its body. */
  sentEmail(logId) {
    return this._get('/v1/quoteFollowups/sentEmails/' + logId)
  }

  // ---- manual enrolment ----
  enrolList(quoteNos) {
    return this._send('post', '/v1/quoteFollowups/enrolList', { quoteNos: quoteNos })
  }

  // ---- one pass now (settings users) ----
  /** `{ sent, skipped, failed, stopped, escalated, note? }`; 409 when a pass is already running. */
  runNow() {
    return this._send('post', '/v1/quoteFollowups/runNow')
  }

  _get(path) {
    return new Promise(async function (resolve, reject) {
      try {
        var response = await axios.get(path)
        resolve(response.data)
      } catch (err) {
        reject(err)
      }
    })
  }

  _send(method, path, body) {
    return new Promise(async function (resolve, reject) {
      try {
        var response = body === undefined ? await axios[method](path) : await axios[method](path, body)
        resolve(response.data)
      } catch (err) {
        reject(err)
      }
    })
  }
}

/** One line for the user from a failed follow-up call. */
export function errorMessage(err, fallback) {
  var data = err && err.response && err.response.data
  if (data && typeof data.error === 'string' && data.error) return data.error
  if (data && data.errors && typeof data.errors === 'object') {
    var msgs = Object.keys(data.errors).map(function (k) { return data.errors[k] })
    if (msgs.length) return msgs.join(' ')
  }
  return fallback || 'Something went wrong. Please try again.'
}

/** 400 field errors as `{ field: message }`, or {}. */
export function fieldErrors(err) {
  var data = err && err.response && err.response.data
  return (data && data.errors && typeof data.errors === 'object') ? data.errors : {}
}
